/**
 * @fileoverview Human-readable audit summary and field-label helpers.
 * @module audit/summaries
 *
 * Responsibilities:
 * - Build end-user summaries for create/update/delete/status/member events.
 * - Describe keyed permit and building-system mutations using injected identities.
 * - Map stored field paths to display labels (contactIds → Contacts).
 * - Detect autosave-only patches so mobile can skip noisy audit rows.
 *
 * Dependencies:
 * - ./types (AuditAction)
 *
 * Architecture:
 * - Pure helpers. No I/O. Shared by mobile-pwa writes and AuditLogTimeline.
 *
 * Notes:
 * - Tenant-web services already write human summaries; mobile uses these helpers
 *   so inspector rows match the same language on tenant-web Audit tabs.
 */

import type { AuditAction, ChangeDetail } from './types';

/**
 * @contract AuditSummaryInput
 * Purpose: Before/after snapshots and keyed changes used to phrase an audit summary.
 * Fields: collection, action, document, beforeDocument, changes, arrayIdentityByField, extra.
 * Rules: Host-specific identities are injected; the audit package does not depend on core-domain.
 * Sync: N/A — input only.
 */
export interface AuditSummaryInput {
  collection: string;
  action: AuditAction;
  document?: Record<string, unknown> | null;
  beforeDocument?: Record<string, unknown> | null;
  changes?: ChangeDetail[];
  arrayIdentityByField?: Readonly<Record<string, (item: unknown, index: number) => string | undefined>>;
  extra?: {
    contactName?: string;
    entityName?: string;
    coInspectorJoined?: string;
  };
}

const AUTOSAVE_ONLY_KEYS = new Set([
  'responses',
  'updatedAt',
  'lock',
  'lockId',
  'lockOwner',
  'lockHeartbeatAt',
  '__optimistic',
  '_syncSeq',
]);

const AUTOSAVE_SKIP_COLLECTIONS = new Set(['cases', 'inspectionCases']);

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  displayName: 'Name',
  entityName: 'Location',
  entityCategory: 'Category',
  permits: 'Operating permits',
  systems: 'Building systems',
  responsiblePartyName: 'Responsible party',
  squareFeet: 'Square footage',
  occupantLoad: 'Occupant load',
  occupancyType: 'Occupancy classification',
  constructionType: 'Construction type',
  knoxBoxLocation: 'Knox Box location',
  caseStatus: 'Status',
  seriesStatus: 'Series status',
  caseNumber: 'Case number',
  caseType: 'Case type',
  fiscalYear: 'Fiscal year',
  contactIds: 'Contacts',
  caseId: 'Inspection',
  events: 'Events',
  responses: 'Inspection answers',
  parcelNumber: 'Parcel number',
  address: 'Address',
  status: 'Status',
  tags: 'Tags',
  notes: 'Notes',
  email: 'Email',
  phone: 'Phone',
  phoneNumbers: 'Phone numbers',
  emails: 'Emails',
  company: 'Company',
  title: 'Title',
  defaultRoles: 'Roles',
  inspectionAccountId: 'Inspection account',
  inspectionAccountName: 'Inspection account',
  moduleViolationsDraft: 'Violations draft',
  violations: 'Violations',
  violation: 'Violation',
  occurrences: 'Occurrences',
  signatures: 'Signatures',
  deliveryChannel: 'Delivery method',
  deliveryStatus: 'Delivery status',
  reportGeneration: 'Report generation',
  assignedTo: 'Assigned to',
  primaryInspectorId: 'Primary inspector',
  primaryInspectorName: 'Primary inspector',
  secondaryInspectorIds: 'Co-inspectors',
  secondaryInspectorNames: 'Co-inspectors',
};

const COLLECTION_NOUN: Record<string, string> = {
  cases: 'inspection',
  inspectionCases: 'inspection',
  entities: 'location',
  inspectableEntities: 'location',
  contacts: 'contact',
  templates: 'template',
  forms: 'form',
  lookups: 'lookup',
  users: 'user',
};

/**
 * @process isAutosaveOnlyPatch
 *
 * Steps:
 * 1. Return false when the collection is not a case feed.
 * 2. Ignore system keys that always change on write.
 * 3. Return true when remaining keys are only responses / lock heartbeat.
 *
 * Inputs:
 * - collection: Dexie or audit collection name.
 * - patch: fields being written.
 *
 * Outputs:
 * - true when the write must not produce an audit row.
 *
 * Failure Handling: never throws.
 * Idempotency: REQUIRED.
 */
export function isAutosaveOnlyPatch(
  collection: string,
  patch: Record<string, unknown> | null | undefined,
): boolean {
  if (!patch) return false;
  if (!AUTOSAVE_SKIP_COLLECTIONS.has(collection)) return false;
  const keys = Object.keys(patch);
  if (keys.length === 0) return false;
  if (keys.some((key) => !AUTOSAVE_ONLY_KEYS.has(key))) return false;
  return keys.some((key) => key === 'responses' || key.startsWith('lock'));
}

/**
 * @process shouldHideAuditField
 * Steps: Recognize opaque identifiers and raw inspection responses that should not appear as field rows.
 * Inputs: Stored ChangeDetail field path.
 * Outputs: True when the field should be hidden from end-user tables.
 * Failure Handling: Empty and unknown paths remain visible.
 * Idempotency: REQUIRED.
 */
export function shouldHideAuditField(field: string): boolean {
  if (!field) return false;
  if (field === 'caseId' || field.endsWith('.caseId')) return true;
  if (field === 'responses') return true;
  const leaf = field.includes('.') ? field.slice(field.lastIndexOf('.') + 1) : field;
  return looksLikeGeneratedFieldId(leaf);
}

function looksLikeGeneratedFieldId(value: string): boolean {
  return /^(fld[_-]|field-\d)/i.test(value) || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(value);
}

/**
 * @process formatAuditFieldLabel
 * Steps: Resolve known labels, then title-case the stored field path.
 * Inputs: Stored ChangeDetail field path.
 * Outputs: Display label for the audit timeline or detail table.
 * Failure Handling: Empty paths return the generic Field label.
 * Idempotency: REQUIRED.
 */
export function formatAuditFieldLabel(field: string): string {
  if (!field) return 'Field';
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  if (/[ ?]/.test(field) && !field.includes('.')) return field;
  const segments = field.split('.').map((segment) => segment.replace(/\[[^\]]*\]/g, ''));
  const leaf = segments[segments.length - 1];
  if (/[ ?]/.test(leaf)) return leaf;
  const root = segments[0];
  if (root !== leaf && FIELD_LABELS[root]) {
    return `${FIELD_LABELS[root]} › ${FIELD_LABELS[leaf] ?? titleCase(leaf)}`;
  }
  if (FIELD_LABELS[leaf]) {
    const parent = segments.slice(0, -1).join('.');
    if (!parent) return FIELD_LABELS[leaf];
    if (parent === 'responses') return FIELD_LABELS[leaf];
    return `${titleCase(parent)} › ${FIELD_LABELS[leaf]}`;
  }
  if (looksLikeGeneratedFieldId(leaf)) return 'Inspection answer';
  return segments.map(titleCase).join(' › ');
}

function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function shortId(id: string): string {
  if (!id) return '';
  return id.length > 12 ? id.slice(0, 8) : id;
}

/**
 * @process getAuditDocumentLabel
 *
 * Steps:
 * 1. Prefer name / displayName / entityName / caseNumber / parcelNumber / email.
 * 2. Fall back to a shortened document id.
 *
 * Inputs:
 * - document: before or after snapshot.
 * - fallbackId: documentId.
 *
 * Outputs:
 * - Human label for summaries.
 *
 * Failure Handling: never throws.
 * Idempotency: REQUIRED.
 */
export function getAuditDocumentLabel(
  document: Record<string, unknown> | null | undefined,
  fallbackId = '',
): string {
  if (!document) return shortId(fallbackId) || 'record';
  const candidates = [
    document.entityName,
    document.name,
    document.displayName,
    document.caseNumber,
    document.parcelNumber,
    document.email,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return shortId(fallbackId) || 'record';
}

function collectionNoun(collection: string): string {
  return COLLECTION_NOUN[collection] ?? collection;
}

function caseTypeLabel(document: Record<string, unknown> | null | undefined): string {
  const caseType = document?.caseType;
  if (caseType && typeof caseType === 'object') {
    const typed = caseType as Record<string, unknown>;
    const name = typed.name ?? typed.label ?? typed.code;
    if (typeof name === 'string' && name.trim()) return name.trim();
  }
  if (typeof caseType === 'string' && caseType.trim()) return caseType.trim();
  return '';
}

/**
 * @process buildAuditSummary
 *
 * Steps:
 * 1. Resolve a human record label from the document.
 * 2. Phrase the action (Created / Updated / Completed / Added contact).
 *
 * Inputs:
 * - AuditSummaryInput.
 *
 * Outputs:
 * - End-user sentence for AuditLogEntry.summary.
 *
 * Failure Handling: never throws.
 * Idempotency: REQUIRED.
 */
export function buildAuditSummary(input: AuditSummaryInput): string {
  const { collection, action, extra } = input;
  const document = input.document ?? null;
  const label = extra?.entityName
    ?? getAuditDocumentLabel(document, String(document?._id ?? ''));
  const noun = collectionNoun(collection);
  const typeName = caseTypeLabel(document);
  const fy = typeof document?.fiscalYear === 'number' || typeof document?.fiscalYear === 'string'
    ? ` FY${document?.fiscalYear}`
    : '';
  const typeSuffix = typeName ? ` — ${typeName}${fy}` : fy ? ` —${fy}` : '';

  if (collection === 'cases' || collection === 'inspectionCases') {
    const coInspectorSummary = summarizeCoInspectorChange(input);
    if (coInspectorSummary) return coInspectorSummary;
  }

  if (collection === 'entities' || collection === 'inspectableEntities') {
    const permitSummary = summarizeChangedItem(input, 'permits', 'permit', label);
    if (permitSummary) return permitSummary;
    const systemSummary = summarizeChangedItem(input, 'systems', 'building system', label);
    if (systemSummary) return systemSummary;
  }

  switch (action) {
    case 'create':
      if (collection === 'cases' || collection === 'inspectionCases') {
        return `Created inspection: ${label}${typeSuffix}`;
      }
      if (collection === 'contacts') {
        return `Created contact: ${label}`;
      }
      if (collection === 'entities' || collection === 'inspectableEntities') {
        return `Created location: ${label}`;
      }
      return `Created ${noun}: ${label}`;
    case 'delete':
      if (collection === 'cases' || collection === 'inspectionCases') {
        return `Deleted inspection: ${label}${typeSuffix}`;
      }
      if (collection === 'contacts') {
        return `Deleted contact: ${label}`;
      }
      return `Deleted ${noun}: ${label}`;
    case 'statusChange':
      if (collection === 'cases' || collection === 'inspectionCases') {
        const status = String(document?.caseStatus ?? document?.seriesStatus ?? '').trim();
        if (status === 'caseCompleted' || status === 'completed') {
          return `Completed inspection: ${label}${typeSuffix}`;
        }
        if (status === 'closedOut') {
          return `Closed out inspection: ${label}${typeSuffix}`;
        }
        if (status === 'archived' || document?._archived === true) {
          return `Archived inspection: ${label}${typeSuffix}`;
        }
        return `Updated inspection status: ${label}${typeSuffix}`;
      }
      return `Changed status of ${noun}: ${label}`;
    case 'addMember':
      return extra?.contactName
        ? `Added contact ${extra.contactName} to ${label}`
        : `Added contact to ${label}`;
    case 'removeMember':
      return extra?.contactName
        ? `Removed contact ${extra.contactName} from ${label}`
        : `Removed contact from ${label}`;
    case 'archive':
      return `Archived ${noun}: ${label}`;
    case 'restore':
      return `Restored ${noun}: ${label}`;
    default:
      if (collection === 'cases' || collection === 'inspectionCases') {
        return `Updated inspection: ${label}${typeSuffix}`;
      }
      if (collection === 'contacts') {
        return `Updated contact: ${label}`;
      }
      if (collection === 'entities' || collection === 'inspectableEntities') {
        return `Updated location: ${label}`;
      }
      return `Updated ${noun}: ${label}`;
  }
}

/**
 * @process summarizeCoInspectorChange
 * Steps: Compare before/after inspector-name projections and phrase the first roster delta.
 * Inputs: Audit snapshots and optional join context.
 * Outputs: A specific add/remove/join summary, or null when no roster name changed.
 * Failure Handling: Missing snapshots are treated as empty rosters.
 * Idempotency: REQUIRED.
 */
function summarizeCoInspectorChange(input: AuditSummaryInput): string | null {
  const before = inspectorNames(input.beforeDocument);
  const after = inspectorNames(input.document);
  const remaining = [...before];
  const added = after.find((name) => {
    const index = remaining.indexOf(name);
    if (index < 0) return true;
    remaining.splice(index, 1);
    return false;
  });
  if (added) {
    return input.extra?.coInspectorJoined === added
      ? `${added} joined as secondary inspector`
      : `Added co-inspector ${added}`;
  }

  const current = [...after];
  const removed = before.find((name) => {
    const index = current.indexOf(name);
    if (index < 0) return true;
    current.splice(index, 1);
    return false;
  });
  return removed ? `Removed co-inspector ${removed}` : null;
}

function inspectorNames(document: Record<string, unknown> | null | undefined): string[] {
  if (!document) return [];
  const names = Array.isArray(document.secondaryInspectorNames)
    ? document.secondaryInspectorNames
    : Array.isArray(document.events)
      ? document.events.flatMap((event) => {
          const record = asRecord(event);
          return Array.isArray(record?.secondaryInspectorNames) ? record.secondaryInspectorNames : [];
        })
      : [];
  return names.filter((name): name is string => typeof name === 'string' && Boolean(name.trim()));
}

/**
 * @process summarizeChangedItem
 * Steps: Resolve a keyed permit or system change and phrase its lifecycle action.
 * Inputs: Audit summary input, array field, item noun, and parent document label.
 * Outputs: A specific add, update, or remove sentence, or null when unchanged.
 * Failure Handling: Incomplete identities use generic item labels.
 * Idempotency: REQUIRED.
 */
function summarizeChangedItem(
  input: AuditSummaryInput,
  field: 'permits' | 'systems',
  noun: 'permit' | 'building system',
  documentLabel: string,
): string | null {
  const change = input.changes?.find((entry) => entry.field.startsWith(`${field}[`));
  if (!change) return null;
  const match = new RegExp(`^${field}\\[([^\\]]+)\\]`).exec(change.field);
  if (!match) return null;

  const identity = match[1];
  const itemField = `${field}[${identity}]`;
  const isItemAdded = change.field === itemField && change.oldValue == null && change.newValue != null;
  const isItemRemoved = change.field === itemField && change.oldValue != null && change.newValue == null;
  const beforeItems = Array.isArray(input.beforeDocument?.[field]) ? input.beforeDocument?.[field] as unknown[] : [];
  const afterItems = Array.isArray(input.document?.[field]) ? input.document?.[field] as unknown[] : [];
  const identityFor = input.arrayIdentityByField?.[field];
  const findItem = (items: unknown[]) => items.find((item, index) => {
    if (field === 'systems') {
      return typeof item === 'object'
        && item !== null
        && String((item as Record<string, unknown>).systemId ?? '') === identity;
    }
    return identityFor?.(item, index) === identity;
  });
  const item = (isItemRemoved ? findItem(beforeItems) : findItem(afterItems))
    ?? findItem(beforeItems)
    ?? (isItemAdded || isItemRemoved
      ? (isItemAdded ? change.newValue : change.oldValue)
      : undefined);
  const itemLabel = field === 'permits' ? permitSummaryLabel(item) : systemSummaryLabel(item);
  const verb = isItemAdded ? 'Added' : isItemRemoved ? 'Removed' : 'Updated';
  const relation = isItemAdded ? 'to' : isItemRemoved ? 'from' : 'at';
  return `${verb} ${noun} ${itemLabel} ${relation} ${documentLabel}`;
}

/**
 * @process permitSummaryLabel
 * Steps: Select the best human-readable permit identifier from its snapshot.
 * Inputs: A permit snapshot or an unknown value.
 * Outputs: Permit number, name, type, or the generic operating-permit label.
 * Failure Handling: Returns a generic label for incomplete snapshots.
 * Idempotency: REQUIRED.
 */
function permitSummaryLabel(value: unknown): string {
  const permit = asRecord(value);
  return firstString(
    permit?.permitNumber,
    permit?.permit_no,
    permit?.name,
    permit?.displayName,
    permit?.permitTypeName,
    nestedName(permit?.permitType),
  ) ?? 'Operating permit';
}

/**
 * @process systemSummaryLabel
 * Steps: Select the best human-readable label from a building-system snapshot.
 * Inputs: A system snapshot or an unknown value.
 * Outputs: System name, type, category, or a generic building-system label.
 * Failure Handling: Returns a generic label for incomplete snapshots.
 * Idempotency: REQUIRED.
 */
function systemSummaryLabel(value: unknown): string {
  const system = asRecord(value);
  const category = typeof system?.category === 'string' ? titleCase(system.category) : undefined;
  return firstString(
    system?.name,
    system?.displayName,
    system?.label,
    system?.description,
    nestedName(system?.systemType),
    category,
  ) ?? 'Building system';
}

/**
 * @process asRecord
 * Steps: Narrow a non-array object to a string-keyed record.
 * Inputs: Any value.
 * Outputs: The record or null.
 * Failure Handling: Returns null for primitives, null, and arrays.
 * Idempotency: REQUIRED.
 */
function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

/**
 * @process nestedName
 * Steps: Read the display name from a nested lookup-shaped value.
 * Inputs: Any value.
 * Outputs: Name, label, or code when the value is a record.
 * Failure Handling: Returns undefined when no display field exists.
 * Idempotency: REQUIRED.
 */
function nestedName(value: unknown): unknown {
  const record = asRecord(value);
  return record?.name ?? record?.label ?? record?.code;
}

/**
 * @process firstString
 * Steps: Return the first non-empty string from candidate display values.
 * Inputs: Ordered unknown candidates.
 * Outputs: A trimmed string or undefined.
 * Failure Handling: Ignores non-string and whitespace-only values.
 * Idempotency: REQUIRED.
 */
function firstString(...values: unknown[]): string | undefined {
  return values.find((value): value is string => typeof value === 'string' && value.trim().length > 0)?.trim();
}
